import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { formatCurrency } from '../../src/lib/comissaoUtils';

interface ConfirmarAuditoriaModalProps {
  faturaIds: string[];
  /** Soma da parte da Vigor dos boletos marcados — é o que a auditoria confere. */
  valorTotal: number;
  onClose: () => void;
  onConfirmed: () => void;
}

/**
 * Confirmação ao marcar boleto(s) como auditado(s). Desde 22/10/2026 vale a palavra de
 * quem audita: não pede mais número, valor nem anexo de Nota Fiscal. Fica gravado quem
 * confirmou e quando.
 */
const ConfirmarAuditoriaModal: React.FC<ConfirmarAuditoriaModalProps> = ({ faturaIds, valorTotal, onClose, onConfirmed }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const quantidade = faturaIds.length === 1 ? '1 boleto' : `${faturaIds.length} boletos`;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('faturas')
        .update({
          auditado_pagamento: true,
          auditado_pagamento_em: new Date().toISOString(),
          auditado_pagamento_por_id: profile?.id,
          auditado_pagamento_por_nome: profile?.full_name,
        })
        .in('id', faturaIds);
      if (error) throw error;

      logUsageEvent({ userId: profile?.id, modulo: 'financeiro', funcionalidade: 'auditoria_pagamentos', acao: 'auditar_pagamento', origem: 'acao' });
      await logAction(profile?.id, faturaIds.length > 1 ? 'AUDITAR_PAGAMENTO_LOTE' : 'AUDITAR_PAGAMENTO', {
        fatura_ids: faturaIds,
        quantidade: faturaIds.length,
        valor_vigor: valorTotal,
      });

      addToast(`${faturaIds.length} boleto(s) marcado(s) como auditado(s).`, 'success');
      onConfirmed();
    } catch (error: any) {
      console.error('Error confirming auditoria:', error);
      addToast(error.message || 'Erro ao confirmar auditoria.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 border dark:border-gray-700 animate-fade-in">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Confirmar auditoria</h3>
          <button onClick={onClose} disabled={saving} className="p-1.5 -mt-1 -mr-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {quantidade} · {formatCurrency(valorTotal)} da Vigor
        </p>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
          Você conferiu {faturaIds.length === 1 ? 'esse pagamento' : 'esses pagamentos'}? Fica registrado com o seu nome e a data de hoje.
        </p>

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl bg-primary text-white font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Confirmando...' : 'Confirmar auditoria'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmarAuditoriaModal;
